import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GetMeResponse, UpdateEmailRequest } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';
import { addOrUpdateMeCache } from '../queries';

type UseRequestEmailChangeActionData = {
    requestEmailChange: (data: UpdateEmailRequest) => Promise<GetMeResponse>;
    loading: boolean;
    error: string;
};

/**
 * Asking to change the signed-in user's email address. The address does not
 * move until the link sent to the new one is followed.
 */
export function useRequestEmailChangeAction(): UseRequestEmailChangeActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `PATCH /v3/me/email` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INCORRECT_PASSWORD')) {
                return t('actions.request_email_change.error_wrong_password');
            }
            if (isApiError(error, 'EMAIL_ALREADY_IN_USE')) {
                return t('actions.request_email_change.error_email_in_use');
            }
            if (isApiError(error, 'EMAIL_BLOCKED')) {
                return t('actions.request_email_change.error_email_blocked');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdateEmailRequest) =>
            openApiRef.current.me
                .requestEmailChange(data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateMeCache(queryClient, data.data);

            UIToast.success(t('actions.request_email_change.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        requestEmailChange: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
